<template>
  <LoadIcon
    v-if="!exportItems || discoveringFields"
    class="d-flex justify-center mt-10"
    data-testid="loading-fields"
  />
  <div
    v-else-if="!exportTabs?.length"
    class="mt-10"
    data-testid="no-fields"
  >
    No fields were detected.
  </div>
  <div
    v-else
    class="export"
  >
    <TabTray
      :tabs="exportTabs"
      class="mt-7"
      @tab-selected="activeTabId = $event"
    />
    <div class="export-wrapper">
      <div
        class="py-2"
        style="margin-left: 33px;"
      >
        <v-btn
          variant="plain"
          color="primary"
          class="text-decoration-underline opacity-100"
          @click="updateAllSelections(tabContent, true)"
        >
          Select All
        </v-btn>
        <v-btn
          variant="plain"
          color="primary"
          class="text-decoration-underline opacity-100"
          @click="updateAllSelections(tabContent, false)"
        >
          Clear
        </v-btn>
      </div>
      <v-list
        v-if="tabContent"
        v-model:opened="openedGroups"
        density="compact"
        bg-color="transparent"
        class="py-0"
      >
        <ExportSettingOptions
          :tab-content="tabContent"
          @update-setting="updateSetting"
        />
      </v-list>
    </div>
  </div>
</template>

<script>
// Components
import TabTray from '@/components/Reusable/TabTray/TabTray.vue';
import ExportSettingOptions from './ExportSettingOptions.vue';
import LoadIcon from '@/components/svgs/LoadIcon.vue';
// Helpers
import {
  applySavedSelections,
  getExportableFields,
  loadExportFieldSelections,
} from '@/lib/classes/SharedMap';
import {collectOpenGroupPaths} from './exportSettingTree';
import {getExportTabs} from './exportTabs';
import {mapActions} from 'vuex';

/**
 * @typedef {import('@/lib/classes/SharedMap/exportFields').ExportFieldSetting} ExportFieldSetting
 * @typedef {import('@/lib/classes/SharedMap/exportFields').ExportFieldSettings} ExportFieldSettings
 * @typedef {import('@/lib/classes/SharedMap/exportFields').ExportSettingsTab} ExportSettingsTab
 */

export default {
  name: 'ExportMapSettings',
  components: {
    TabTray,
    ExportSettingOptions,
    LoadIcon,
  },
  props: {
    /** @type {ExportFieldSettings} */
    exportFieldSettings: {
      type: Map,
      required: true,
    },
    /** Hydrated items per collection name, or null while the dialog is still gathering them. */
    exportItems: {
      type: Map,
      default: null,
    },
  },
  emits: ['setSettings', 'discovering'],
  data() {
    return {
      /** @type {string} */
      activeTabId: null,
      /** @type {ExportSettingsTab[]} */
      tabs: [],
      /** @type {string[]} */
      openedGroups: [],
      buildCount: 0,
      /** True while field discovery is in flight; gates the spinner and, via emit, the dialog's buttons. */
      discoveringFields: false,
    };
  },
  computed: {
    /** @return {ExportSettingsTab[]} */
    exportTabs() {
      return this.tabs.filter((tab) => this.exportFieldSettings.has(tab.id));
    },
    /** @return {ExportFieldSettings[]} */
    tabContent() {
      return this.exportFieldSettings.get(this.activeTabId);
    },
  },
  watch: {
    tabContent: {
      immediate: true,
      handler(content) {
        this.openedGroups = collectOpenGroupPaths(content);
      },
    },
    // Rebuild whenever the dialog delivers a fresh gather, including on re-open.
    exportItems: {
      immediate: true,
      handler() {
        void this.buildExportSettings();
      },
    },
  },
  methods: {
    ...mapActions(['addMessage']),
    /** Discovers the exportable fields and tabs for the gathered items. */
    async buildExportSettings() {
      // A fresh gather supersedes any build still in flight; the stale build must not touch state.
      const buildCount = ++this.buildCount;
      const superseded = () => buildCount !== this.buildCount;

      this.tabs = [];
      this.activeTabId = null;
      if (!this.exportItems) return;
      // `exportItems` arriving is what kicks off discovery, so stay in the loading state until it finishes.
      this.discoveringFields = true;
      this.$emit('discovering', true);

      // Discovery is independent per collection and the saved selections depend on neither, so run
      // them all concurrently instead of awaiting one collection at a time.
      const savedSelections = loadExportFieldSelections();
      const discovered = await Promise.all([...this.exportItems].map(async ([collectionName, items]) => {
        try {
          return {items, exportableFields: await getExportableFields(collectionName, items)};
        } catch (error) {
          // One bad collection must not blank the whole dialog.
          console.error(`Failed to compute exportable fields for '${collectionName}':`, error);
          return {failed: true, name: collectionName};
        }
      }));
      if (superseded()) return;

      // Merge in the original collection order so tab order is unchanged.
      let exportSettings = new Map();
      const failedCollections = [];
      for (const result of discovered) {
        if (result.failed) {
          failedCollections.push(result.name);
          continue;
        }
        if (!result.exportableFields?.size) continue;
        exportSettings = new Map([...exportSettings, ...result.exportableFields]);
        const newTabs = getExportTabs(result.items, result.exportableFields)
          .filter((tab) => !this.tabs.some((existing) => existing.id === tab.id));
        this.tabs.push(...newTabs);
      }
      // Tell the user which layers were dropped; an empty-but-clean gather stays "No fields were detected".
      if (failedCollections.length) {
        this.addMessage({
          header: 'Some map data could not be loaded',
          messageType: 'danger',
          body: 'These layers were left out of the export:',
          bulletedList: failedCollections,
        });
      }

      // Prepopulate from the user's last export.
      const saved = await savedSelections;
      if (superseded()) return;
      if (saved) {
        applySavedSelections(exportSettings, saved);
      }
      this.$emit('setSettings', exportSettings);
      // Ensure the exportFieldSettings have been updated before setting the active tab
      await this.$nextTick();
      if (superseded()) return;

      this.activeTabId = this.exportTabs.length ? this.exportTabs[0]?.id : null;
      // Only the winning (non-superseded) build clears the loading state it set.
      this.discoveringFields = false;
      this.$emit('discovering', false);
    },
    /**
     * Updates the value of a setting in exportFieldSettings and all of its children settings.
     * @param {string} label - The full path for the label to update separated by periods. ex: 'Lease Deal.Lease Type'
     * @param {boolean} value - The new value of the setting
     */
    updateSetting({label, value}) {
      const nestedLabels = label.split('.');
      /** @type {ExportFieldSetting} */
      let tabSettings = this.exportFieldSettings.get(this.activeTabId);

      if (!tabSettings) return;

      // Find the setting to update
      nestedLabels.forEach((nestedLabel, index) => {
        const setting = tabSettings.find((s) => s.label === nestedLabel);

        if (index === nestedLabels.length - 1) {
          // Once the setting is found, update the value and recursively update the children settings
          setting.value = value;
          if (setting.children) {
            this.updateAllSelections(setting.children, value);
          }
        } else {
          // Continue traversing the nested settings until we reach the setting to update
          tabSettings = setting.children;
        }
      });

      // Check if the parent setting(s) should be updated to indeterminate
      this.updateParentIndeterminateState(nestedLabels);
    },
    /**
     * Updates the parent setting to be indeterminate if its children are not all true or all false.
     * @param {string[]} nestedLabels - The labels of the nested settings to update
     */
    updateParentIndeterminateState(nestedLabels) {
      const updateParent = (labels) => {
        if (labels.length <= 1) return; // If there are no more parent settings to update, return

        const parentSetting = this.tabContent.find((setting) => setting.label === labels[0]);
        const childValues = parentSetting.children.reduce((acc, child) => {
          acc.push(child.value);
          return acc;
        }, []);

        const allFalse = childValues.every((val) => val === false);
        const allTrue = childValues.every((val) => val === true);

        // If the parent setting has a mix of true and false children, set the parent setting to indeterminate.
        if (allTrue) {
          parentSetting.value = true;
        } else if (allFalse) {
          parentSetting.value = false;
        } else {
          parentSetting.value = 'indeterminate';
        }
        updateParent(labels.slice(0, -1));
      };
      updateParent(nestedLabels);
    },
    /**
     * Select all settings
     * @param {ExportFieldSetting[]} settings - The settings to update
     * @param {boolean} selected - Whether to select all or clear all
     */
    updateAllSelections(settings, selected) {
      if (!settings) return;

      function selectAllSettings(settings) {
        settings.forEach((setting) => {
          // Update the value of the setting and recursively update the children settings
          setting.value = selected;
          if (setting.children) {
            selectAllSettings(setting.children);
          }
        });
      }
      selectAllSettings(settings);
    },
  },
};
</script>

<style lang="scss" scoped>
.export {
  height: 355px;

  &-wrapper {
    height: 315px;
    overflow-y: scroll;
    padding-bottom: 10px;
    background-color: rgb(242, 242, 242, 0.5);
  }
}
</style>
